import { useState } from "react";
import type { ProductOpaqueValue } from "@b2bportal/email-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { BookingType, CallState } from "@overrides/types";
import { getResendEmailType, selfServeApi } from "@overrides/utilities";
import {
  SelfServeRawEventName,
  useEmitCoreSelfServeRawEvent,
  useEmitCoreSelfServeRawEventOnce,
} from "../../tracking";

export function useResendEmail(
  reservationId: string,
  reservationType: BookingType,
  isLuxuryHotel = false
) {
  const { t } = useI18nContext();
  const [reqState, setReqState] = useState(CallState.NotCalled);
  const emitConfirmResendEmailEvent = useEmitCoreSelfServeRawEvent(
    SelfServeRawEventName.ConfirmResendEmail
  );

  const onSubmit = async (email: string) => {
    const product: ProductOpaqueValue = {
      type: reservationType,
      value: {
        value: reservationId,
      },
    };
    const emailType = getResendEmailType(reservationType, isLuxuryHotel);

    // note - per b2b-interfaces, flights is a special case where the opaque value is just the reservationId
    if (reservationType === BookingType.Flight) {
      product.value = reservationId;
    }

    setReqState(CallState.InProcess);
    emitConfirmResendEmailEvent({
      properties: {
        screen: reservationType,
      },
    });

    try {
      await selfServeApi.apiV0EmailResendPost({
        emailType,
        product,
        emailAddress: email,
      });

      setReqState(CallState.Success);
    } catch (err) {
      setReqState(CallState.Failed);
    }
  };

  const onTryAgain = () => setReqState(CallState.NotCalled);

  useEmitCoreSelfServeRawEventOnce({
    name: SelfServeRawEventName.ViewedResendEmailModal,
    properties: {
      screen: reservationType,
    },
  });

  return {
    copy: {
      closeBtn: t("close.button"),
      emailForm: {
        inputLabel: t("email"),
        submitBtn: t("core-self-serve.resend-email.submit"),
        subtitle: t("core-self-serve.resend-email.form.subtitle"),
      },
      error: {
        subtitle: t("core-self-serve.resend-email.error.subtitle"),
        title: t("core-self-serve.resend-email.error.title"),
        tryAgainBtn: t("core-self-serve.resend-email.try-again"),
      },
      processing: t("core-self-serve.resend-email.processing"),
      success: {
        subtitle: t("core-self-serve.resend-email.success.subtitle"),
        title: t("core-self-serve.resend-email.success.title"),
      },
    },
    context: { reqState },
    handlers: { onSubmit, onTryAgain, setReqState },
  };
}

export type IResendEmailData = ReturnType<typeof useResendEmail>;
